<template>
  <div class="page-outer banners-list-page">
    <div class="page-header page-header-banners-list">
      <div class="header-left">
        <h1 class="page-title"><i class="ri-image-line"></i> Banner 管理</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="goToCreate">
          <i class="ri-add-line"></i> 新增 Banner
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state"><div class="spinner"></div><p>載入中...</p></div>
    <div v-else-if="error" class="error-state">{{ error }}</div>
    <div v-else class="page-container">
      <!-- 版位分類（Tab，樣式對齊商城設定） -->
      <div class="banner-placement-tabs-nav" role="tablist" aria-label="Banner 版位分類">
        <button
          v-for="p in PLACEMENT_OPTIONS"
          :key="p.value"
          type="button"
          role="tab"
          class="banner-placement-tab-btn"
          :class="{ active: currentPlacement === p.value }"
          :aria-selected="currentPlacement === p.value ? 'true' : 'false'"
          @click="setPlacement(p.value)"
        >
          <i :class="PLACEMENT_TAB_ICONS[p.value]" aria-hidden="true"></i>
          {{ p.label }}
        </button>
      </div>

      <div v-if="!banners.length" class="empty-state">
        <i class="ri-image-line empty-icon"></i>
        <p>此版位尚無 Banner。</p>
      </div>
      <div v-else class="table-wrapper">
        <table class="table">
          <thead>
            <tr>
              <th>預覽</th>
              <th>版位</th>
              <th>連結</th>
              <th>排序</th>
              <th>狀態</th>
              <th>有效期間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in banners" :key="b.id">
              <td>
                <img :src="b.image_url" class="banner-thumb" :alt="b.alt_text || 'banner'" />
              </td>
              <td><span class="badge">{{ getPlacementLabel(b.placement) }}</span></td>
              <td class="banner-href">{{ b.href || '（無連結）' }}</td>
              <td>{{ b.sort_order }}</td>
              <td>
                <span class="status-badge" :class="b.is_active ? 'active' : 'inactive'">
                  {{ b.is_active ? '啟用' : '停用' }}
                </span>
              </td>
              <td class="banner-dates">
                <span v-if="b.valid_from || b.valid_to">
                  {{ b.valid_from ? formatDate(b.valid_from) : '—' }} ~ {{ b.valid_to ? formatDate(b.valid_to) : '—' }}
                </span>
                <span v-else class="text-muted">長期有效</span>
              </td>
              <td>
                <div class="action-btns">
                  <button type="button" class="btn btn-secondary btn-sm" @click="goToEdit(b)"><i class="ri-edit-line"></i> 編輯</button>
                  <button type="button" class="btn btn-danger btn-sm" @click="confirmDelete(b)"><i class="ri-delete-bin-line"></i></button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageTitle } from '@/composables/usePageTitle.js'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'
import { showError, showSuccess, showDeleteConfirm } from '@/utils/swal.js'

usePageTitle('Banner 管理')

const route = useRoute()
const router = useRouter()

const PLACEMENT_OPTIONS = [
  { value: 'home', label: '首頁' },
  { value: 'category', label: '分類頁' },
  { value: 'product', label: '商品頁' },
  { value: 'storefront_page', label: '前台頁面' },
  { value: 'hall', label: '館別頁' },
  { value: 'checkout', label: '結帳頁' },
  { value: 'announcement', label: '公告欄' },
]

const PLACEMENT_TAB_ICONS = {
  home: 'ri-home-4-line',
  category: 'ri-folder-line',
  product: 'ri-shopping-bag-3-line',
  storefront_page: 'ri-file-text-line',
  hall: 'ri-store-2-line',
  checkout: 'ri-shopping-cart-line',
  announcement: 'ri-megaphone-line',
}

const currentPlacement = ref('home')
const banners = ref([])
const loading = ref(false)
const error = ref('')

function getPlacementLabel(v) { return PLACEMENT_OPTIONS.find((p) => p.value === v)?.label ?? v }
function formatDate(d) { return new Date(d).toLocaleDateString('zh-TW') }

function applyPlacementFromQuery() {
  const p = route.query.placement
  if (typeof p === 'string' && PLACEMENT_OPTIONS.some((o) => o.value === p)) {
    currentPlacement.value = p
  }
}

async function loadBanners() {
  loading.value = true
  error.value = ''
  try {
    const res = await commerceSettingsApi.listBanners(currentPlacement.value)
    banners.value = res?.data?.items ?? []
  } catch (e) {
    error.value = e?.response?.data?.message || '載入失敗'
  } finally {
    loading.value = false
  }
}

watch(
  () => ({ name: route.name, placement: route.query.placement }),
  (curr) => {
    if (curr.name !== 'ClientCommerceBanners') return
    applyPlacementFromQuery()
    loadBanners()
  },
  { immediate: true }
)

function setPlacement(p) {
  currentPlacement.value = p
  loadBanners()
}

function goToCreate() {
  router.push({ path: '/client/commerce/banners/create', query: { placement: currentPlacement.value } })
}

function goToEdit(b) {
  router.push(`/client/commerce/banners/${encodeURIComponent(b.id)}/edit`)
}

async function confirmDelete(b) {
  const confirmed = await showDeleteConfirm('確定刪除此 Banner？')
  if (!confirmed) return
  try {
    await commerceSettingsApi.deleteBanner(b.id)
    await showSuccess('Banner 已刪除')
    await loadBanners()
  } catch (e) {
    await showError(e?.response?.data?.message || '刪除失敗')
  }
}
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  margin-bottom: 0;
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-actions { display: flex; align-items: center; gap: 8px; }

.page-header .page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

/* 與 CommerceSettings「商城設定」分頁 Tab 同一套視覺 */
.banner-placement-tabs-nav {
  display: flex;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow-x: auto;
  margin-bottom: 16px;
  -webkit-overflow-scrolling: touch;
}

.banner-placement-tab-btn {
  flex: 1;
  min-width: 88px;
  padding: 14px 16px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}

.banner-placement-tab-btn:hover {
  background: #f9fafb;
  color: #374151;
}

.banner-placement-tab-btn.active {
  color: #667eea;
  border-bottom-color: #667eea;
  background: #f8f9ff;
}

.banner-placement-tab-btn i {
  font-size: 18px;
}

.banner-thumb {
  width: 80px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
}
.banner-href {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #6b7280;
}
.banner-dates { font-size: 12px; color: #6b7280; }
.text-muted { color: #9ca3af; }
.badge {
  background: #e0e7ff;
  color: #4338ca;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
}
.status-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
}
.status-badge.active { background: #d1fae5; color: #065f46; }
.status-badge.inactive { background: #f3f4f6; color: #6b7280; }
.action-btns { display: flex; gap: 4px; flex-wrap: wrap; align-items: center; }

.table-wrapper table { min-width: 800px; }
</style>

<style>
.page-header-banners-list {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}
.sidebar.collapsed ~ .main-content .page-header-banners-list {
  left: 64px;
}
@media (max-width: 768px) {
  .page-header-banners-list {
    left: 0 !important;
    top: 56px;
  }
}
</style>
